import React from 'react';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {NAVY, headCell, bodyCell} from './widgets.comp';
import {rowsFrom} from '../../DashboardCommon/dashboardCards';
import {EvidenceCell, RecommendationCell} from '../../DashboardCommon/RecommendationCells.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/**
 * The National Priority Actions table, shared by the two Priority Actions pages and the
 * Performance Summary's highlighted tables.
 *
 * A National PriorityTableCard (ND.S_15.C_2 / S_16.C_2, and S_14.C_6 / C_7 in highlight mode)
 * carries Priority · Region · School · Baseline Evidence · Recommended Learning | Actions — no
 * Country column: a National dashboard is one partner's schools in ONE country, so the hierarchy
 * is Region → School. Baseline Evidence arrives as separate lists ({readiness, cyber_risk,
 * context}) and is drawn by the shared EvidenceCell; the slides title that column "Areas of
 * Weakness".
 *
 * QA, 9 Oct 2026: Region and School sort and filter from their headers (the old drop-down filter
 * bar is gone); the recommendation column sorts. Pass `query` / `onQuery` to keep the page's
 * search box — it is drawn in the toolbar above the table.
 */

/** The empty-table copy for a dashboard whose own population is under the n floor: rows for such units are not built upstream. */
export const SUPPRESSED_EMPTY = 'Priority actions are not shown for groups of fewer than 10 students.';

/** Rows in this page's shape; `recommendationHeader` is the card's last column name. */
export const priorityRows = (card, recommendationHeader) => rowsFrom(card, {
    region: 'Region', school: 'School', evidence: 'Baseline Evidence', recommendation: recommendationHeader,
});

const SEARCH_PLACEHOLDER = 'Search by region, school…';

const columnsFor = (recommendationHeader) => [
    {key: 'region', label: 'Region', type: 'text'},
    {key: 'school', label: 'School', type: 'text'},
    // The slides rename the card's Baseline Evidence column (Ian, 1 Oct). Its value is grouped lists — neither sorted nor filtered.
    {key: 'evidence', label: 'Areas of Weakness', type: 'text', sortable: false, filterable: false},
    // One string per row ("Area: program; …") — sorts, groups by area; too varied to filter.
    {key: 'recommendation', label: recommendationHeader, type: 'text', filterable: false},
];

export const PriorityTable = ({rows, recommendationHeader, emptyText = 'No recommendations yet.', query, onQuery}) => {
    const columns = React.useMemo(() => columnsFor(recommendationHeader), [recommendationHeader]);
    const t = useTableControls(rows, columns);
    const searching = typeof onQuery === 'function';
    return (
        <>
            {searching
                ? <TableToolbar {...t} query={query} onQuery={onQuery} placeholder={SEARCH_PLACEHOLDER}/>
                : (t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('region')} sx={{...headCell, textAlign: 'left', pl: 2.5}}/>
                            <SortFilterHeaderCell {...t.headerProps('school')} sx={{...headCell, textAlign: 'left'}}/>
                            <SortFilterHeaderCell {...t.headerProps('evidence')} sx={{...headCell, textAlign: 'left'}}/>
                            <SortFilterHeaderCell {...t.headerProps('recommendation')} sx={{...headCell, textAlign: 'left'}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>No row matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={rows.indexOf(row)} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.region}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><RecommendationCell row={row}/></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};
